import { getInvoicePDFFromInvoiceId } from "@/apis/custom/customRoutes";
import * as Sentry from "@sentry/react";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Maximum number of PDFs to keep in cache.
 * Each PDF blob can be several MB, so limiting to 15 keeps memory reasonable.
 * With ~2-5MB average per PDF, this caps usage at ~30-75MB for the cache.
 */
const MAX_CACHE_SIZE = 15;

type PdfCache = Record<string, string>;
type FetchResult = { url: string; blob: string };

type InvoicePdfCacheContextValue = {
  getPdfBlob: (invoiceId: string) => string | null;
  fetchPdf: (invoiceId: string) => Promise<FetchResult>;
  fetchPdfUrl: (invoiceId: string) => Promise<string>;
  clearCache: () => void;
  clearSingleCache: (invoiceId: string) => void;
  getCacheSize: () => number;
};

const InvoicePdfCacheContext = createContext<
  InvoicePdfCacheContextValue | undefined
>(undefined);

type Props = PropsWithChildren;

export const InvoicePdfCacheProvider = ({ children }: Props) => {
  const [pdfCache, setPdfCache] = useState<PdfCache>({});
  const inFlightRef = useRef<Record<string, Promise<FetchResult>>>({});
  const cacheRef = useRef<PdfCache>({});

  // Track access times in a ref to avoid re-renders on access
  const accessTimesRef = useRef<Record<string, number>>({});

  // Keep cacheRef in sync for cleanup on unmount
  useEffect(() => {
    cacheRef.current = pdfCache;
  }, [pdfCache]);

  // Cleanup all blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(cacheRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  /**
   * Evicts the least recently accessed entries to maintain cache size limit.
   * Uses accessTimesRef to determine LRU order.
   */
  const evictIfNeeded = useCallback((cache: PdfCache): PdfCache => {
    const keys = Object.keys(cache);
    if (keys.length <= MAX_CACHE_SIZE) {
      return cache;
    }

    // Sort keys by access time (oldest first)
    const sortedKeys = keys.sort((a, b) => {
      const timeA = accessTimesRef.current[a] ?? 0;
      const timeB = accessTimesRef.current[b] ?? 0;
      return timeA - timeB;
    });

    // Calculate how many to evict
    const evictCount = keys.length - MAX_CACHE_SIZE;
    const keysToEvict = sortedKeys.slice(0, evictCount);

    // Revoke URLs for evicted entries
    for (const key of keysToEvict) {
      URL.revokeObjectURL(cache[key]);
      delete accessTimesRef.current[key];
    }

    // Return new cache without evicted entries
    const newCache: PdfCache = {};
    for (const key of sortedKeys.slice(evictCount)) {
      newCache[key] = cache[key];
    }
    return newCache;
  }, []);

  const getPdfBlob = useCallback(
    (invoiceId: string) => {
      const cached = pdfCache[invoiceId];
      if (!cached) return null;

      // Update access time in ref (no re-render)
      accessTimesRef.current[invoiceId] = Date.now();

      return cached;
    },
    [pdfCache],
  );

  const fetchPdf = useCallback(
    async (invoiceId: string): Promise<FetchResult> => {
      const cached = pdfCache[invoiceId];
      if (cached) {
        // Update access time in ref (no re-render)
        accessTimesRef.current[invoiceId] = Date.now();

        const url = await getInvoicePDFFromInvoiceId(invoiceId);
        return { url, blob: cached };
      }

      // Check if already fetching
      const existing = inFlightRef.current[invoiceId];
      if (existing) return existing;

      const promise = (async () => {
        try {
          const url = await getInvoicePDFFromInvoiceId(invoiceId);
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF (${response.status})`);
          }

          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);

          // Update access time
          accessTimesRef.current[invoiceId] = Date.now();

          setPdfCache((prev) => {
            const newCache = {
              ...prev,
              [invoiceId]: objectUrl,
            };
            // Evict old entries if over limit
            return evictIfNeeded(newCache);
          });

          return { url, blob: objectUrl };
        } catch (error) {
          Sentry.captureException(error);
          throw error;
        } finally {
          delete inFlightRef.current[invoiceId];
        }
      })();

      inFlightRef.current[invoiceId] = promise;
      return promise;
    },
    [pdfCache, evictIfNeeded],
  );

  const clearCache = useCallback(() => {
    Object.values(pdfCache).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    accessTimesRef.current = {};
    setPdfCache({});
  }, [pdfCache]);

  const clearSingleCache = useCallback(
    (invoiceId: string) => {
      const existing = pdfCache[invoiceId];
      if (!existing) return;

      URL.revokeObjectURL(existing);
      delete accessTimesRef.current[invoiceId];
      setPdfCache((prev) => {
        const { [invoiceId]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [pdfCache],
  );

  const getCacheSize = useCallback(() => {
    return Object.keys(pdfCache).length;
  }, [pdfCache]);

  const value = useMemo(
    () => ({
      getPdfBlob,
      fetchPdf,
      fetchPdfUrl: getInvoicePDFFromInvoiceId,
      clearCache,
      clearSingleCache,
      getCacheSize,
    }),
    [getPdfBlob, fetchPdf, clearCache, clearSingleCache, getCacheSize],
  );

  return (
    <InvoicePdfCacheContext.Provider value={value}>
      {children}
    </InvoicePdfCacheContext.Provider>
  );
};

export const useInvoicePdfCache = () => {
  const context = useContext(InvoicePdfCacheContext);
  if (!context) {
    throw new Error(
      "useInvoicePdfCache must be used within a InvoicePdfCacheProvider",
    );
  }
  return context;
};
